import React, { useState } from 'react';
import {
  Card,
  CardBody,
  Button,
  FormGroup,
  Label,
  InputGroup,
  InputGroupText,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert
} from 'reactstrap';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import DataHandler from '../../../services/DataHandler';
import { useTranslation } from 'react-i18next';
import { FaGlobeAmericas } from 'react-icons/fa'; // Icono para el selector de países


/**
 * Cuarto paso del wizard de Activar Cuenta.
 *
 * En este paso el usuario confirma su identidad ingresando su número de celular.
 * Se enviará un SMS con un código de verificación al número proporcionado.
 */
const RegistroCuentaPaso4SMS = ({ registrationId, onNext, onBack }) => {
  /**
   *  Validación con Yup
   * - Debe iniciar con 0
   * - Solo dígitos
   * - Campo obligatorio
   */

  const [errorModal, setErrorModal] = useState({ open: false, msg: '' });
  const [successModal, setSuccessModal] = useState({ open: false, mobile: '' });

  // i18n – namespace "activation"
  const { t, i18n } = useTranslation('activation');

  const validationSchema = Yup.object({
    telefono: Yup.string()
      .matches(/^0\d+$/, t('step4.errors.phoneFormat'))
      .required(t('step4.errors.phoneRequired')),
  });


  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'es';

  // Catálogo de países (solo Ecuador y EE.UU. habilitados)
  const countries = [
    { code: '+593', name: 'Ecuador', flag: '🇪🇨', enabled: true, maxDigits: 10 },
    { code: '+1', name: 'United States', flag: '🇺🇸', enabled: true, maxDigits: 10 },
    { code: '+52', name: 'México', flag: '🇲🇽', enabled: false, maxDigits: 10 },
    { code: '+54', name: 'Argentina', flag: '🇦🇷', enabled: false, maxDigits: 10 },
    { code: '+56', name: 'Chile', flag: '🇨🇱', enabled: false, maxDigits: 9 },
  ];
  const [selectedCountry, setSelectedCountry] = useState(countries[0]); // Ecuador por defecto


  return (
    <>
      <Card className="shadow-sm">
        <CardBody>
          {/* 🔹 Título del paso */}
          <h5 className="mb-4">{t('step4.title')}</h5>

          <Formik
            initialValues={{ telefono: '0' }} // Comienza con '0' por el formato local
            validationSchema={validationSchema}
            onSubmit={async ({ telefono }, { setSubmitting, setStatus }) => {
              setStatus(null);
              try {
                const movil = selectedCountry.code + telefono;
                const dh = new DataHandler(
                  'POSTREGISTROENVIOSMS',
                  { id: registrationId, lang: currentLang }, // Añadido: parámetro de idioma
                  null,
                  () => { },
                  () => { },
                  () => { }
                );
                const result = await dh.postData({
                  id: registrationId,
                  telefonomovil: movil,
                });

                console.log('Respuesta teléfono:', result);
                if (result.succeeded) {
                  // éxito: mostrar modal
                  setSuccessModal({ open: true, mobile: result.data });
                } else {
                  // error de negocio: mostrar modal
                  setErrorModal({
                    open: true,
                    msg: result.message || t('step4.errorModal.genericBody'),
                  });
                }
              } catch (err) {
                setErrorModal({
                  open: true,
                  msg: t('step4.errors.connectionError'),
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, status, setFieldValue }) => (
              <Form>
                <FormGroup>
                  <Label for="telefono">{t('step4.mobileLabel')}</Label>
                  <div className="d-flex flex-column flex-sm-row">  {/* Columna en móviles, fila en sm+ */}
                    <div className="mb-2 mb-sm-0" style={{ width: '100%', maxWidth: '150px' }}>  {/* Contenedor del selector */}
                      <InputGroup>
                        <InputGroupText style={{ width: '100%' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <FaGlobeAmericas />
                            <select
                              value={selectedCountry.code}
                              onChange={(e) => {
                                const country = countries.find(c => c.code === e.target.value);
                                setSelectedCountry(country);
                                setFieldValue('telefono', country.code === '+593' ? '0' : '');
                              }}
                              style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%' }}
                            >
                              {countries.map((country) => (
                                <option key={country.code} value={country.code} disabled={!country.enabled}>
                                  {country.flag} {country.name} ({country.code})
                                </option>
                              ))}
                            </select>
                          </div>
                        </InputGroupText>
                      </InputGroup>
                    </div>
                    <div style={{ width: '100%' }}>  {/* Contenedor del campo de texto */}
                      <Field
                        name="telefono"
                        type="text"
                        as={Input}
                        placeholder={t('step4.mobilePlaceholder')}
                        className="form-control"
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^0-9]/g, '');
                          if (selectedCountry.code === '+593') {
                            if (value.startsWith('0') && value.length > 10) value = value.slice(0, 10);
                            if (!value.startsWith('0') && value.length > 9) value = value.slice(0, 9);
                          } else {
                            if (value.length > selectedCountry.maxDigits) value = value.slice(0, selectedCountry.maxDigits);
                          }
                          e.target.value = value;
                          setFieldValue('telefono', value);
                        }}
                      />
                    </div>
                  </div>
                </FormGroup>



                {/* Por si en algún momento decides usar status para errores generales */}
                {status?.error && (
                  <Alert color="danger" className="mt-2">
                    {status.error}
                  </Alert>
                )}

                <div className="d-flex justify-content-between mt-4">
                  <Button color="secondary" onClick={onBack} disabled={isSubmitting}>
                    {t('common.previous')}
                  </Button>
                  <Button color="primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? t('common.saving') : t('step4.submitButton')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </CardBody>
      </Card>

      {/* Modal de error */}
      <Modal
        isOpen={errorModal.open}
        toggle={() => setErrorModal({ open: false, msg: '' })}
        centered
      >
        <ModalHeader
          toggle={() => setErrorModal({ open: false, msg: '' })}
        >
          {t('step4.errorModal.title')}
        </ModalHeader>
        <ModalBody>
          {errorModal.msg || t('step4.errorModal.genericBody')}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setErrorModal({ open: false, msg: '' })}
          >
            {t('step4.errorModal.close')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal de éxito */}
      <Modal
        isOpen={successModal.open}
        toggle={() => setSuccessModal({ open: false, mobile: '' })}
        centered
      >
        <ModalHeader
          toggle={() => setSuccessModal({ open: false, mobile: '' })}
        >
          {t('step4.successModal.title')}
        </ModalHeader>
        <ModalBody>
          {/* Texto partido en prefix + número en negrita + suffix para respetar i18n */}
          {t('step4.successModal.prefix')}{' '}
          <strong>{successModal.mobile}</strong>{' '}
          {t('step4.successModal.suffix')}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              setSuccessModal({ open: false, mobile: '' });
              onNext();
            }}
          >
            {t('step4.successModal.continue')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default RegistroCuentaPaso4SMS;
